/**
 * Pure transition detection for the bulk-validation job poller.
 *
 * The active-job endpoint returns the LATEST job in any status (or null when
 * the campaign has never run one), and the poller must decide when a terminal
 * job is *news* (the run the user just watched finished) versus *history*
 * (a page load showing last week's completed job). Kept free of React/query
 * imports so it can be contract-tested in the node-only vitest environment.
 */

/** What the poller remembers about the previous fetch. */
export interface JobSnapshot {
  jobId: number | null
  running: boolean
}

interface JobLike {
  id: number
  status: string
}

export const isJobRunning = (job: JobLike | null | undefined): boolean =>
  job?.status === 'pending' || job?.status === 'in_progress'

/**
 * Decide whether the newly fetched job just finished from the user's
 * perspective.
 *
 * @param seen - Snapshot from the previous fetch of this hook instance;
 *   `undefined` means this is the first fetch since mount.
 * @param job - The job returned by the current fetch (null = no job ever).
 * @returns The terminal status to announce ('completed' | 'failed'), or null
 *   when there is nothing to announce.
 *
 * Branches:
 * - First fetch after mount → null. A terminal job at mount is history.
 * - Job missing or still running → null.
 * - Same job we previously saw running → announce (classic transition).
 * - A job id we never saw at all (including campaigns whose previous fetch
 *   returned no job) → announce: it started and finished between fetches.
 *   This covers sub-2s jobs that complete before the poller's first tick.
 * - Same job id we only ever saw terminal → null (already announced).
 */
export function evaluateJobTransition(
  seen: JobSnapshot | undefined,
  job: JobLike | null
): 'completed' | 'failed' | null {
  if (seen === undefined || job === null || isJobRunning(job)) return null
  if (job.status !== 'completed' && job.status !== 'failed') return null
  const justFinished = seen.jobId === job.id ? seen.running : true
  return justFinished ? job.status : null
}
